import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api } from '@/utils/api';
import { Plus, Edit2, Trash2 } from 'lucide-react';
import Layout from '@/components/Layout';
import EnterpriseTable from '@/components/EnterpriseTable';
import ModuleIntelligenceBlock from '@/components/ModuleIntelligenceBlock';
import { toast } from 'sonner';

export default function Properties() {
  const { t } = useTranslation();
  const [portfolios, setPortfolios] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProperty, setEditingProperty] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [formData, setFormData] = useState({
    name: '', name_ar: '', address: '', address_ar: '', city: '',
    portfolio_id: '', property_type: 'residential',
  });

  React.useEffect(() => {
    api.getPortfolios().then(r => setPortfolios(r.data || [])).catch(() => {});
  }, []);

  const fetcher = useCallback(async (params) => {
    const { data } = await api.getPropertiesPaginated(params);
    return data;
  }, [reloadKey]); // eslint-disable-line

  const resetForm = () => {
    setFormData({ name: '', name_ar: '', address: '', address_ar: '', city: '', portfolio_id: '', property_type: 'residential' });
    setEditingProperty(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingProperty) await api.updateProperty(editingProperty.id, formData);
      else await api.createProperty(formData);
      setDialogOpen(false);
      resetForm();
      setReloadKey(k => k + 1);
      toast.success(editingProperty ? 'Property updated' : 'Property created');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to save property');
    }
  };

  const handleEdit = (property) => {
    setEditingProperty(property);
    setFormData({
      name: property.name,
      name_ar: property.name_ar || '',
      address: property.address,
      address_ar: property.address_ar || '',
      city: property.city,
      portfolio_id: property.portfolio_id,
      property_type: property.property_type,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (property) => {
    if (!window.confirm(`Delete property "${property.name}"?`)) return;
    try {
      await api.deleteProperty(property.id);
      setReloadKey(k => k + 1);
      toast.success('Property deleted');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to delete');
    }
  };

  const columns = [
    { id: 'name',         header: t('propertyName'),  accessor: 'name',         minWidth: 220,
      render: (v, r) => (<span className="font-medium text-[#0E1320]">{v}{r.name_ar && <span className="ms-2 text-[11px] text-[#71777E]">{r.name_ar}</span>}</span>) },
    { id: 'address',      header: t('address'),       accessor: 'address',      minWidth: 220 },
    { id: 'city',         header: t('city'),          accessor: 'city',         minWidth: 120 },
    { id: 'property_type',header: t('propertyType'),  accessor: 'property_type',minWidth: 130,
      render: (v) => (
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium reva-mono ${
          v === 'residential' ? 'bg-emerald-100 text-emerald-800' :
          v === 'commercial' ? 'bg-blue-100 text-blue-800' :
          v === 'mixed' ? 'bg-amber-100 text-amber-800' :
          'bg-[#F1F4F7] text-[#4A5263]'
        }`}>{v}</span>
      ) },
    { id: 'total_units',  header: t('totalUnits'),    accessor: 'total_units',  minWidth: 100, render: v => <span className="reva-mono">{(v ?? 0).toLocaleString()}</span> },
    { id: 'occupancy_rate', header: t('occupancyRate'), accessor: 'occupancy_rate', minWidth: 120, render: v => <span className="reva-mono">{v ?? 0}%</span> },
  ];

  return (
    <Layout>
      <div className="space-y-5" data-testid="properties-page">
        <div className="flex justify-between items-center gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-[#0E1320]">{t('properties')}</h1>
            <p className="text-[13px] text-[#71777E] mt-1">Portfolio operations · search · filter · export · bulk actions.</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={resetForm} data-testid="properties-add">
                <Plus className="mr-2 h-4 w-4" /> {t('addProperty')}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>{editingProperty ? t('edit') : t('add')} {t('properties')}</DialogTitle></DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>{t('propertyName')} (EN)*</Label>
                    <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required data-testid="property-name" /></div>
                  <div><Label>{t('propertyName')} (AR)</Label>
                    <Input value={formData.name_ar} onChange={(e) => setFormData({ ...formData, name_ar: e.target.value })} /></div>
                  <div><Label>{t('address')} (EN)*</Label>
                    <Input value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} required /></div>
                  <div><Label>{t('address')} (AR)</Label>
                    <Input value={formData.address_ar} onChange={(e) => setFormData({ ...formData, address_ar: e.target.value })} /></div>
                  <div><Label>{t('city')}*</Label>
                    <Input value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} required /></div>
                  <div><Label>{t('portfolios')}*</Label>
                    <Select value={formData.portfolio_id} onValueChange={(value) => setFormData({ ...formData, portfolio_id: value })} required>
                      <SelectTrigger><SelectValue placeholder="Select portfolio" /></SelectTrigger>
                      <SelectContent>{portfolios.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                    </Select></div>
                  <div><Label>{t('propertyType')}*</Label>
                    <Select value={formData.property_type} onValueChange={(value) => setFormData({ ...formData, property_type: value })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="residential">{t('residential')}</SelectItem>
                        <SelectItem value="commercial">{t('commercial')}</SelectItem>
                        <SelectItem value="mixed">{t('mixed')}</SelectItem>
                      </SelectContent>
                    </Select></div>
                </div>
                <Button type="submit" className="w-full">{t('save')}</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <EnterpriseTable
          key={reloadKey}
          columns={columns}
          fetcher={fetcher}
          filterDefs={[
            { id: 'property_type', label: 'Property type', options: [
              { value: 'residential', label: 'Residential' },
              { value: 'commercial', label: 'Commercial' },
              { value: 'mixed', label: 'Mixed' },
            ]},
            { id: 'city', label: 'City', options: [
              { value: 'Riyadh', label: 'Riyadh' },
              { value: 'Jeddah', label: 'Jeddah' },
              { value: 'Dammam', label: 'Dammam' },
              { value: 'Khobar', label: 'Khobar' },
              { value: 'Makkah', label: 'Makkah' },
              { value: 'Madinah', label: 'Madinah' },
            ]},
          ]}
          rowActions={[
            { id: 'edit', label: 'Edit', icon: <Edit2 size={12} />, onClick: handleEdit },
            { id: 'delete', label: 'Delete', icon: <Trash2 size={12} className="text-red-600" />, onClick: handleDelete },
          ]}
          storageKey="reva_properties_table"
          emptyHint="No properties yet. Click + Add Property to get started."
          testId="properties-table"
        />

        <ModuleIntelligenceBlock module="properties" testId="properties-intel" />
      </div>
    </Layout>
  );
}
